// Feature Access (per-company entitlements) — client mirror of the server
// registry in server/src/config/companyFeatures.ts. Keep the two in sync.
//
// Artyc admins decide which gated screens each company can access. To gate a
// new screen: add an entry here, gate its nav item / route with
// companyHasFeature, and add the matching server entry.

export enum CompanyFeature {
  FIELD_PERSONNEL = 'fieldPersonnel',
  AUDIT_TRAIL = 'auditTrail',
  JOURNEY_INSIGHTS = 'journeyInsights',
  ARTYC_AI = 'artycAi',
  LIVE_RISK = 'liveRisk',
  EXTERNAL_CONTEXT = 'externalContext',
}

export interface CompanyFeatureDef {
  key: CompanyFeature;
  // Label shown in the Artyc edit-company "Feature Access" panel.
  label: string;
  defaultEnabled: boolean;
}

export const COMPANY_FEATURES: CompanyFeatureDef[] = [
  {
    key: CompanyFeature.FIELD_PERSONNEL,
    label: 'Field Personnel',
    defaultEnabled: false,
  },
  {
    key: CompanyFeature.AUDIT_TRAIL,
    label: '21 CFR Part 11',
    defaultEnabled: false,
  },
  {
    key: CompanyFeature.JOURNEY_INSIGHTS,
    label: 'Journey Insights',
    defaultEnabled: false,
  },
  {
    key: CompanyFeature.ARTYC_AI,
    label: 'Artyc AI',
    defaultEnabled: false,
  },
  {
    key: CompanyFeature.LIVE_RISK,
    label: 'Live Risk',
    defaultEnabled: false,
  },
  {
    key: CompanyFeature.EXTERNAL_CONTEXT,
    label: 'Weather & Traffic',
    defaultEnabled: false,
  },
];

export type FeatureAccess = Partial<Record<CompanyFeature, boolean>>;

// Whether a company may access a feature, falling back to the feature's default
// when it has no explicit setting.
export const companyHasFeature = (
  featureAccess: FeatureAccess | undefined | null,
  feature: CompanyFeature
): boolean => {
  if (featureAccess && feature in featureAccess) {
    return Boolean(featureAccess[feature]);
  }
  return (
    COMPANY_FEATURES.find((f) => f.key === feature)?.defaultEnabled ?? false
  );
};
